import React, { useState, useEffect } from 'react';
import {
  Box,
  Container,
  Card,
  CardContent,
  Typography,
  Avatar,
  List,
  ListItem,
  ListItemText,
  ListItemAvatar,
  Chip,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Alert,
  Tabs,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Pagination,
  LinearProgress,
  CircularProgress
} from '@mui/material';
import Grid from '@mui/material/Grid';
import {
  People,
  Business,
  EventNote,
  Dashboard,
  Analytics,
  Settings,
  Edit,
  Delete,
  AttachMoney,
  CalendarMonth,
  ViewList,
  FilterList
} from '@mui/icons-material';
import { useAuth } from '../contexts/AuthContext';
import axios from 'axios';

// Import Recharts components
import {
  LineChart,
  Line,
  PieChart,
  Pie,
  Cell,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  ComposedChart,
  Bar
} from 'recharts';

interface DashboardStats {
  totalUsers: number;
  totalBusinesses: number;
  totalReservations: number;
  totalBoards: number;
  totalRevenue: string;
}

interface User {
  id: number;
  name: string;
  email: string;
  role: string;
  subscriptionPlan: string;
  phone?: string;
  createdAt: string;
  updatedAt: string;
  businesses?: any[];
}

interface Business {
  id: number;
  name: string;
  description?: string;
  ownerId: number;
  createdAt: string;
  updatedAt: string;
  owner?: {
    id: number;
    name: string;
    email: string;
  };
  boards?: any[];
}

interface Board {
  id: number;
  name: string;
  type: string;
  businessId: number;
  settings?: any;
  createdAt: string;
  updatedAt: string;
  business?: {
    id: number;
    name: string;
    owner?: {
      id: number;
      name: string;
      email: string;
    };
  };
  reservationCount?: number;
}

interface Reservation {
  id: number;
  clientName: string;
  clientEmail: string;
  clientPhone: string;
  startDate: string;
  endDate: string;
  status: string;
  boardId: number;
  createdAt: string;
  updatedAt: string;
  board?: {
    id: number;
    name: string;
    type: string;
    business?: {
      id: number;
      name: string;
    };
  };
}

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

const TabPanel = (props: TabPanelProps) => {
  const { children, value, index, ...other } = props;
  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`super-admin-tabpanel-${index}`}
      aria-labelledby={`super-admin-tab-${index}`}
      {...other}
    >
      {value === index && (
        <Box sx={{ p: 3 }}>
          {children}
        </Box>
      )}
    </div>
  );
};

// Analytics Panel Component
const AnalyticsPanel: React.FC = () => {
  const [analyticsData, setAnalyticsData] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [dateRange, setDateRange] = useState('30');

  useEffect(() => {
    fetchAnalyticsData();
  }, [dateRange]);

  const fetchAnalyticsData = async () => {
    try {
      setLoading(true);
      const endDate = new Date();
      const startDate = new Date();
      startDate.setDate(startDate.getDate() - parseInt(dateRange));

      const response = await axios.get('/api/superadmin/analytics', {
        params: {
          startDate: startDate.toISOString(),
          endDate: endDate.toISOString(),
          granularity: 'day'
        }
      });

      const data = response.data;

      const userGrowth = data.userGrowth?.map((item: any) => ({
        date: item.date,
        count: parseInt(item.count) || 0
      })) || [];

      const reservationStats = data.reservationStats?.map((item: any) => ({
        date: item.date,
        count: parseInt(item.count) || 0,
        revenue: parseFloat(item.revenue) || 0
      })) || [];

      const businessTypes = data.businessTypes?.map((item: any) => ({
        type: item.type || item.name || 'unknown',
        count: parseInt(item.count) || 0
      })) || [];

      const topBusinesses = data.topBusinesses?.map((item: any) => ({
        id: item.id,
        name: item.name || `Business ${item.id}`,
        totalReservations: parseInt(item.totalReservations) || 0
      })) || [];

      setAnalyticsData({
        userGrowth,
        reservationStats,
        businessTypes,
        topBusinesses
      });
    } catch (error) {
      console.error('Error fetching analytics:', error);
      setAnalyticsData({
        userGrowth: [],
        reservationStats: [],
        businessTypes: [],
        topBusinesses: []
      });
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
        <CircularProgress />
        <Typography sx={{ ml: 2 }}>Loading analytics...</Typography>
      </Box>
    );
  }

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h5" fontWeight="bold">
          Platform Analytics 📊
        </Typography>
        <FormControl size="small" sx={{ minWidth: 150 }}>
          <InputLabel>Date Range</InputLabel>
          <Select
            value={dateRange}
            onChange={(e) => setDateRange(e.target.value)}
            label="Date Range"
          >
            <MenuItem value="7">Last 7 days</MenuItem>
            <MenuItem value="30">Last 30 days</MenuItem>
            <MenuItem value="90">Last 90 days</MenuItem>
            <MenuItem value="365">Last year</MenuItem>
          </Select>
        </FormControl>
      </Box>

      <Grid container spacing={3}>
        <Grid size={{ xs: 12 }}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                User Growth 📈
              </Typography>
              <Box sx={{ height: 300, width: '100%' }}>
                <ResponsiveContainer width="100%" height="100%">
                  <LineChart data={analyticsData?.userGrowth || []}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="date" />
                    <YAxis />
                    <Tooltip />
                    <Legend />
                    <Line type="monotone" dataKey="count" stroke="#1976d2" name="New Users" />
                  </LineChart>
                </ResponsiveContainer>
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid size={{ xs: 12, md: 6 }}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Business Types 🏢
              </Typography>
              <Box sx={{ height: 300, width: '100%' }}>
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    <Pie
                      data={analyticsData?.businessTypes || []}
                      dataKey="count"
                      nameKey="type"
                      cx="50%"
                      cy="50%"
                      outerRadius={80}
                      label
                    >
                      {(analyticsData?.businessTypes || []).map((entry: any, index: number) => (
                        <Cell key={`cell-${index}`} fill={['#FF6B9D', '#9B59B6', '#3498DB', '#2ECC71', '#F39C12'][index % 5]} />
                      ))}
                    </Pie>
                    <Tooltip />
                    <Legend />
                  </PieChart>
                </ResponsiveContainer>
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid size={{ xs: 12, md: 6 }}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Reservations & Revenue 📅
              </Typography>
              <Box sx={{ height: 300, width: '100%' }}>
                <ResponsiveContainer width="100%" height="100%">
                  <ComposedChart data={analyticsData?.reservationStats || []}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="date" />
                    <YAxis yAxisId="left" />
                    <YAxis yAxisId="right" orientation="right" />
                    <Tooltip />
                    <Legend />
                    <Bar yAxisId="left" dataKey="count" fill="#0288d1" name="Reservations" />
                    <Line yAxisId="right" type="monotone" dataKey="revenue" stroke="#2e7d32" name="Revenue" />
                  </ComposedChart>
                </ResponsiveContainer>
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </Box>
  );
};

const SuperAdminDashboard: React.FC = () => {
  const { user } = useAuth();
  const [activeTab, setActiveTab] = useState(0);
  const [stats, setStats] = useState<DashboardStats | null>(null);
  const [users, setUsers] = useState<User[]>([]);
  const [businesses, setBusinesses] = useState<Business[]>([]);
  const [boards, setBoards] = useState<Board[]>([]);
  const [reservations, setReservations] = useState<Reservation[]>([]);
  const [recentUsers, setRecentUsers] = useState<User[]>([]);
  const [recentReservations, setRecentReservations] = useState<any[]>([]);
  const [subscriptionStats, setSubscriptionStats] = useState<any>({});
  const [topBusinesses, setTopBusinesses] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadingUsers, setLoadingUsers] = useState(false);
  const [loadingBusinesses, setLoadingBusinesses] = useState(false);
  const [loadingBoards, setLoadingBoards] = useState(false);
  const [loadingReservations, setLoadingReservations] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  // Pagination states
  const [userPage, setUserPage] = useState(1);
  const [totalUserPages, setTotalUserPages] = useState(1);
  const [totalUsers, setTotalUsers] = useState(0);
  const [businessPage, setBusinessPage] = useState(1);
  const [totalBusinessPages, setTotalBusinessPages] = useState(1);
  const [totalBusinesses, setTotalBusinesses] = useState(0);
  const [boardPage, setBoardPage] = useState(1);
  const [totalBoardPages, setTotalBoardPages] = useState(1);
  const [totalBoards, setTotalBoards] = useState(0);
  const [reservationPage, setReservationPage] = useState(1);
  const [totalReservationPages, setTotalReservationPages] = useState(1);
  const [totalReservations, setTotalReservations] = useState(0);
  const [boardTypes, setBoardTypes] = useState<string[]>([]);

  const [editUserDialog, setEditUserDialog] = useState(false);
  const [deleteUserDialog, setDeleteUserDialog] = useState(false);
  const [userToDelete, setUserToDelete] = useState<User | null>(null);
  const [selectedUser, setSelectedUser] = useState<User | null>(null);
  const [userForm, setUserForm] = useState({
    name: '',
    email: '',
    role: '',
    subscriptionPlan: '',
    phone: ''
  });

  const [userFilters, setUserFilters] = useState({
    search: '',
    role: '',
    subscription: '',
    sortBy: 'createdAt',
    sortOrder: 'DESC'
  });

  const [businessFilters, setBusinessFilters] = useState({
    search: '',
    sortBy: 'createdAt',
    sortOrder: 'DESC'
  });

  const [boardFilters, setBoardFilters] = useState({
    search: '',
    type: '',
    sortBy: 'createdAt',
    sortOrder: 'DESC'
  });

  const [reservationFilters, setReservationFilters] = useState({
    search: '',
    status: '',
    boardId: '',
    startDate: '',
    endDate: '',
    sortBy: 'createdAt',
    sortOrder: 'DESC'
  });

  useEffect(() => {
    const currentUser = user as any;
    if (currentUser?.role !== 'super_admin') {
      setError('Access denied. Super admin privileges required.');
      return;
    }
    loadDashboardData();
    loadUsers();
    loadBusinesses();
    loadBoards();
    loadReservations();
    loadBoardTypes();
  }, [user]);

  useEffect(() => {
    // Load users when page or filters change (but not on initial load)
    if (user && (userPage !== 1 || Object.values(userFilters).some(val => val !== ''))) {
      loadUsers();
    }
  }, [userPage, userFilters, user]);

  useEffect(() => {
    if (user && (businessPage !== 1 || Object.values(businessFilters).some(val => val !== ''))) {
      loadBusinesses();
    }
  }, [businessPage, businessFilters, user]);

  useEffect(() => {
    if (user && (boardPage !== 1 || Object.values(boardFilters).some(val => val !== ''))) {
      loadBoards();
    }
  }, [boardPage, boardFilters, user]);

  useEffect(() => {
    if (user && (reservationPage !== 1 || Object.values(reservationFilters).some(val => val !== ''))) {
      loadReservations();
    }
  }, [reservationPage, reservationFilters, user]);

  const loadDashboardData = async () => {
    try {
      setLoading(true);
      console.log('Loading dashboard data...');
      const response = await axios.get('/api/superadmin/dashboard');
      const data = response.data;

      setStats(data.overview);
      setRecentUsers(data.recentUsers);
      setRecentReservations(data.recentReservations);
      setSubscriptionStats(data.subscriptionStats);
      setTopBusinesses(data.topBusinesses);

      // Set total counts from dashboard
      setTotalUsers(data.overview?.totalUsers || 0);
      setTotalBusinesses(data.overview?.totalBusinesses || 0);
      setTotalBoards(data.overview?.totalBoards || 0);
      setTotalReservations(data.overview?.totalReservations || 0);

      console.log('Dashboard data loaded successfully');
    } catch (error: any) {
      console.error('Error loading dashboard data:', error);
      setError('Error loading dashboard data');
    } finally {
      setLoading(false);
    }
  };

  const loadUsers = async () => {
    try {
      setLoadingUsers(true);
      console.log('Loading users with filters:', userFilters);
      const response = await axios.get('/api/superadmin/users', {
        params: {
          page: userPage,
          limit: 20,
          ...userFilters
        }
      });

      setUsers(response.data.users);
      setTotalUsers(response.data.pagination.totalUsers);
      setTotalUserPages(response.data.pagination.totalPages);
      console.log(`Loaded ${response.data.users.length} users`);
    } catch (error: any) {
      console.error('Error loading users:', error);
      setError('Error loading users');
    } finally {
      setLoadingUsers(false);
    }
  };

  const loadBusinesses = async () => {
    try {
      setLoadingBusinesses(true);
      console.log('Loading businesses with filters:', businessFilters);
      const response = await axios.get('/api/superadmin/businesses', {
        params: {
          page: businessPage,
          limit: 20,
          ...businessFilters
        }
      });

      setBusinesses(response.data.businesses);
      setTotalBusinesses(response.data.pagination.totalBusinesses);
      setTotalBusinessPages(response.data.pagination.totalPages);
      console.log(`Loaded ${response.data.businesses.length} businesses`);
    } catch (error: any) {
      console.error('Error loading businesses:', error);
      setError('Error loading businesses');
    } finally {
      setLoadingBusinesses(false);
    }
  };

  const loadBoards = async () => {
    try {
      setLoadingBoards(true);
      console.log('Loading boards with filters:', boardFilters);
      const response = await axios.get('/api/superadmin/boards', {
        params: {
          page: boardPage,
          limit: 20,
          ...boardFilters
        }
      });

      setBoards(response.data.boards);
      setTotalBoards(response.data.pagination.totalBoards);
      setTotalBoardPages(response.data.pagination.totalPages);
      console.log(`Loaded ${response.data.boards.length} boards`);
    } catch (error: any) {
      console.error('Error loading boards:', error);
      setError('Error loading boards');
    } finally {
      setLoadingBoards(false);
    }
  };

  const loadReservations = async () => {
    try {
      setLoadingReservations(true);
      console.log('Loading reservations with filters:', reservationFilters);
      const response = await axios.get('/api/superadmin/reservations', {
        params: {
          page: reservationPage,
          limit: 50,
          ...reservationFilters
        }
      });

      setReservations(response.data.reservations);
      setTotalReservations(response.data.pagination.totalReservations);
      setTotalReservationPages(response.data.pagination.totalPages);
      console.log(`Loaded ${response.data.reservations.length} reservations`);
    } catch (error: any) {
      console.error('Error loading reservations:', error);
      setError('Error loading reservations');
    } finally {
      setLoadingReservations(false);
    }
  };

  const loadBoardTypes = async () => {
    try {
      const response = await axios.get('/api/superadmin/board-types');
      setBoardTypes(response.data.types);
    } catch (error: any) {
      console.error('Error loading board types:', error);
    }
  };

  const handleEditUser = (user: User) => {
    setSelectedUser(user);
    setUserForm({
      name: user.name,
      email: user.email,
      role: user.role,
      subscriptionPlan: user.subscriptionPlan,
      phone: user.phone || ''
    });
    setEditUserDialog(true);
  };

  const handleUpdateUser = async () => {
    try {
      await axios.put(`/api/superadmin/users/${selectedUser!.id}`, userForm);
      setSuccess('User updated successfully');
      setEditUserDialog(false);
      loadUsers();
      loadDashboardData();
    } catch (error: any) {
      console.error('Error updating user:', error);
      setError(error.response?.data?.message || 'Error updating user');
    }
  };

  const handleDeleteUser = async () => {
    try {
      await axios.delete(`/api/superadmin/users/${userToDelete!.id}`);
      setSuccess('User deleted successfully');
      setDeleteUserDialog(false);
      setUserToDelete(null);
      loadUsers();
      loadDashboardData();
    } catch (error: any) {
      console.error('Error deleting user:', error);
      setError(error.response?.data?.message || 'Error deleting user');
    }
  };

  const openDeleteDialog = (user: User) => {
    setUserToDelete(user);
    setDeleteUserDialog(true);
  };

  const getRoleColor = (role: string) => {
    switch (role) {
      case 'super_admin': return 'error';
      case 'admin': return 'warning';
      case 'business_owner': return 'primary';
      default: return 'default';
    }
  };

  const getSubscriptionColor = (plan: string) => {
    switch (plan) {
      case 'premium': return 'success';
      case 'basic': return 'info';
      case 'free': return 'default';
      default: return 'default';
    }
  };

  const getStatusColor = (status: string) => {
    switch (status?.toLowerCase()) {
      case 'confirmed': return 'success';
      case 'pending': return 'warning';
      case 'cancelled': return 'error';
      case 'completed': return 'info';
      case 'no_show': return 'error';
      default: return 'default';
    }
  };

  const getBoardTypeColor = (type: string) => {
    switch (type?.toLowerCase()) {
      case 'restaurant': return 'orange';
      case 'barber': return 'blue';
      case 'salon': return 'pink';
      case 'medical': return 'red';
      case 'generic': return 'purple';
      default: return 'default';
    }
  };

  const currentUser = user as any;
  if (currentUser?.role !== 'super_admin') {
    return (
      <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
        <Alert severity="error">
          Access denied. Super admin privileges required.
        </Alert>
      </Container>
    );
  }

  if (loading && !stats) {
    return (
      <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
        <LinearProgress />
        <Typography sx={{ mt: 2, textAlign: 'center' }}>
          Cargando panel de administración...
          <br />
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            Procesando estadísticas del sistema
          </Typography>
        </Typography>
      </Container>
    );
  }

  return (
    <Container maxWidth="xl" sx={{ mt: 4, mb: 4 }}>
      <Box sx={{ mb: 4 }}>
        <Typography variant="h4" fontWeight="bold" sx={{ mb: 1 }}>
          Super Admin Dashboard 🛡️
        </Typography>
        <Typography variant="body1" color="text.secondary">
          Complete platform control and analytics
        </Typography>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}
      {success && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess('')}>
          {success}
        </Alert>
      )}

      <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 3 }}>
        <Tabs value={activeTab} onChange={(e, newValue) => setActiveTab(newValue)}>
          <Tab icon={<Dashboard />} label="Overview" />
          <Tab icon={<People />} label={`Users (${totalUsers})`} />
          <Tab icon={<Business />} label={`Businesses (${totalBusinesses})`} />
          <Tab icon={<CalendarMonth />} label={`Agendas (${totalBoards})`} />
          <Tab icon={<EventNote />} label={`Reservations (${totalReservations})`} />
          <Tab icon={<Analytics />} label="Analytics" />
          <Tab icon={<Settings />} label="System" />
        </Tabs>
      </Box>

      <TabPanel value={activeTab} index={0}>
        <Grid container spacing={3}>
          {stats && (
            <>
              <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                <Card>
                  <CardContent>
                    <Box display="flex" alignItems="center" justifyContent="space-between">
                      <Box>
                        <Typography color="text.secondary" gutterBottom>
                          Total Users
                        </Typography>
                        <Typography variant="h4">
                          {stats.totalUsers.toLocaleString()}
                        </Typography>
                      </Box>
                      <Avatar sx={{ bgcolor: 'primary.main' }}>
                        <People />
                      </Avatar>
                    </Box>
                  </CardContent>
                </Card>
              </Grid>

              <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                <Card>
                  <CardContent>
                    <Box display="flex" alignItems="center" justifyContent="space-between">
                      <Box>
                        <Typography color="text.secondary" gutterBottom>
                          Total Businesses
                        </Typography>
                        <Typography variant="h4">
                          {stats.totalBusinesses.toLocaleString()}
                        </Typography>
                      </Box>
                      <Avatar sx={{ bgcolor: 'success.main' }}>
                        <Business />
                      </Avatar>
                    </Box>
                  </CardContent>
                </Card>
              </Grid>

              <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                <Card>
                  <CardContent>
                    <Box display="flex" alignItems="center" justifyContent="space-between">
                      <Box>
                        <Typography color="text.secondary" gutterBottom>
                          Total Reservations
                        </Typography>
                        <Typography variant="h4">
                          {stats.totalReservations.toLocaleString()}
                        </Typography>
                      </Box>
                      <Avatar sx={{ bgcolor: 'info.main' }}>
                        <EventNote />
                      </Avatar>
                    </Box>
                  </CardContent>
                </Card>
              </Grid>

              <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                <Card>
                  <CardContent>
                    <Box display="flex" alignItems="center" justifyContent="space-between">
                      <Box>
                        <Typography color="text.secondary" gutterBottom>
                          Est. Revenue
                        </Typography>
                        <Typography variant="h4" color="success.main">
                          ${stats.totalRevenue}
                        </Typography>
                      </Box>
                      <Avatar sx={{ bgcolor: 'success.main' }}>
                        <AttachMoney />
                      </Avatar>
                    </Box>
                  </CardContent>
                </Card>
              </Grid>
            </>
          )}

          <Grid size={{ xs: 12, md: 6 }}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Subscription Plans
                </Typography>
                <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', mt: 2 }}>
                  {Object.entries(subscriptionStats).map(([plan, count]) => (
                    <Chip
                      key={plan}
                      label={`${plan.toUpperCase()}: ${count}`}
                      color={getSubscriptionColor(plan) as any}
                      variant="outlined"
                    />
                  ))}
                </Box>
              </CardContent>
            </Card>
          </Grid>

          <Grid size={{ xs: 12, md: 6 }}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Top Businesses
                </Typography>
                <List>
                  {topBusinesses.slice(0, 5).map((business: any, index: number) => (
                    <ListItem key={business.id}>
                      <ListItemAvatar>
                        <Avatar>{index + 1}</Avatar>
                      </ListItemAvatar>
                      <ListItemText
                        primary={business.name}
                        secondary={`${business.reservationCount || 0} reservations • ${business.userCount || 0} boards`}
                      />
                    </ListItem>
                  ))}
                </List>
              </CardContent>
            </Card>
          </Grid>

          <Grid size={{ xs: 12, md: 6 }}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Recent Users
                </Typography>
                <List>
                  {recentUsers.slice(0, 5).map((user) => (
                    <ListItem key={user.id}>
                      <ListItemAvatar>
                        <Avatar>{user.name.charAt(0)}</Avatar>
                      </ListItemAvatar>
                      <ListItemText
                        primary={user.name}
                        secondary={
                          <Box>
                            <Typography variant="body2" component="span">
                              {user.email}
                            </Typography>
                            <Box sx={{ mt: 1 }}>
                              <Chip
                                size="small"
                                label={user.role}
                                color={getRoleColor(user.role) as any}
                                sx={{ mr: 1 }}
                              />
                              <Chip
                                size="small"
                                label={user.subscriptionPlan}
                                color={getSubscriptionColor(user.subscriptionPlan) as any}
                              />
                            </Box>
                          </Box>
                        }
                      />
                    </ListItem>
                  ))}
                </List>
              </CardContent>
            </Card>
          </Grid>

          <Grid size={{ xs: 12, md: 6 }}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Recent Reservations
                </Typography>
                <List>
                  {recentReservations.slice(0, 5).map((reservation) => (
                    <ListItem key={reservation.id}>
                      <ListItemAvatar>
                        <Avatar sx={{ bgcolor: 'info.main' }}>
                          <EventNote />
                        </Avatar>
                      </ListItemAvatar>
                      <ListItemText
                        primary={`Reservation #${reservation.id}`}
                        secondary={
                          <Box>
                            <Typography variant="body2">
                              {new Date(reservation.startDate).toLocaleDateString()}
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                              Status: {reservation.status}
                            </Typography>
                          </Box>
                        }
                      />
                    </ListItem>
                  ))}
                </List>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      </TabPanel>

      <TabPanel value={activeTab} index={1}>
        <Card>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              User Management ({totalUsers} total users)
            </Typography>

            <Box sx={{ display: 'flex', gap: 2, mb: 3, flexWrap: 'wrap' }}>
              <TextField
                size="small"
                label="Search users"
                value={userFilters.search}
                onChange={(e) => setUserFilters({ ...userFilters, search: e.target.value })}
                sx={{ minWidth: 200 }}
              />
              <FormControl size="small" sx={{ minWidth: 150 }}>
                <InputLabel>Role</InputLabel>
                <Select
                  value={userFilters.role}
                  onChange={(e) => setUserFilters({ ...userFilters, role: e.target.value })}
                  label="Role"
                >
                  <MenuItem value="">All Roles</MenuItem>
                  <MenuItem value="super_admin">Super Admin</MenuItem>
                  <MenuItem value="admin">Admin</MenuItem>
                  <MenuItem value="business_owner">Business Owner</MenuItem>
                </Select>
              </FormControl>
              <FormControl size="small" sx={{ minWidth: 150 }}>
                <InputLabel>Subscription</InputLabel>
                <Select
                  value={userFilters.subscription}
                  onChange={(e) => setUserFilters({ ...userFilters, subscription: e.target.value })}
                  label="Subscription"
                >
                  <MenuItem value="">All Plans</MenuItem>
                  <MenuItem value="free">Free</MenuItem>
                  <MenuItem value="basic">Basic</MenuItem>
                  <MenuItem value="premium">Premium</MenuItem>
                </Select>
              </FormControl>
              <Button
                variant="contained"
                onClick={() => {
                  setUserPage(1);
                  loadUsers();
                }}
              >
                Apply Filters
              </Button>
            </Box>

            <TableContainer component={Paper} sx={{ maxHeight: 600 }}>
              {loadingUsers && (
                <Box sx={{ p: 2 }}>
                  <LinearProgress />
                  <Typography variant="body2" sx={{ mt: 1, textAlign: 'center' }}>
                    Cargando usuarios...
                  </Typography>
                </Box>
              )}
              <Table stickyHeader>
                <TableHead>
                  <TableRow>
                    <TableCell>User</TableCell>
                    <TableCell>Role</TableCell>
                    <TableCell>Subscription</TableCell>
                    <TableCell>Businesses</TableCell>
                    <TableCell>Joined</TableCell>
                    <TableCell>Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {users.map((user) => (
                    <TableRow key={user.id}>
                      <TableCell>
                        <Box>
                          <Typography variant="subtitle2">{user.name}</Typography>
                          <Typography variant="body2" color="text.secondary">
                            {user.email}
                          </Typography>
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={user.role}
                          color={getRoleColor(user.role) as any}
                          size="small"
                        />
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={user.subscriptionPlan}
                          color={getSubscriptionColor(user.subscriptionPlan) as any}
                          size="small"
                        />
                      </TableCell>
                      <TableCell>
                        {user.businesses?.length || 0}
                      </TableCell>
                      <TableCell>
                        {new Date(user.createdAt).toLocaleDateString()}
                      </TableCell>
                      <TableCell>
                        <IconButton
                          size="small"
                          onClick={() => handleEditUser(user)}
                          color="primary"
                        >
                          <Edit />
                        </IconButton>
                        {user.role !== 'super_admin' && (
                          <IconButton
                            size="small"
                            onClick={() => openDeleteDialog(user)}
                            color="error"
                          >
                            <Delete />
                          </IconButton>
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>

            <Box sx={{ display: 'flex', justifyContent: 'center', mt: 3 }}>
              <Pagination
                count={totalUserPages}
                page={userPage}
                onChange={(e, page) => setUserPage(page)}
                color="primary"
              />
            </Box>
          </CardContent>
        </Card>
      </TabPanel>

      {/* Businesses Tab */}
      <TabPanel value={activeTab} index={2}>
        <Card>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              Business Management ({totalBusinesses} total businesses)
            </Typography>

            <Box sx={{ display: 'flex', gap: 2, mb: 3, flexWrap: 'wrap' }}>
              <TextField
                size="small"
                label="Search businesses"
                value={businessFilters.search}
                onChange={(e) => setBusinessFilters({ ...businessFilters, search: e.target.value })}
                sx={{ minWidth: 200 }}
              />
              <Button
                variant="contained"
                onClick={() => {
                  setBusinessPage(1);
                  loadBusinesses();
                }}
              >
                Apply Filters
              </Button>
            </Box>

            {loadingBusinesses && (
              <Box sx={{ p: 2 }}>
                <LinearProgress />
                <Typography variant="body2" sx={{ mt: 1, textAlign: 'center' }}>
                  Cargando negocios...
                </Typography>
              </Box>
            )}

            <TableContainer component={Paper} sx={{ maxHeight: 600 }}>
              <Table stickyHeader>
                <TableHead>
                  <TableRow>
                    <TableCell>Business</TableCell>
                    <TableCell>Owner</TableCell>
                    <TableCell>Agendas</TableCell>
                    <TableCell>Created</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {businesses.map((business) => (
                    <TableRow key={business.id}>
                      <TableCell>
                        <Box>
                          <Typography variant="subtitle2">{business.name}</Typography>
                          <Typography variant="body2" color="text.secondary">
                            {business.description || 'No description'}
                          </Typography>
                        </Box>
                      </TableCell>
                      <TableCell>
                        {business.owner ? (
                          <Box>
                            <Typography variant="body2">{business.owner.name}</Typography>
                            <Typography variant="caption" color="text.secondary">
                              {business.owner.email}
                            </Typography>
                          </Box>
                        ) : (
                          <Typography variant="body2" color="text.secondary">No owner</Typography>
                        )}
                      </TableCell>
                      <TableCell>
                        {business.boards?.length || 0}
                      </TableCell>
                      <TableCell>
                        {new Date(business.createdAt).toLocaleDateString()}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>

            <Box sx={{ display: 'flex', justifyContent: 'center', mt: 3 }}>
              <Pagination
                count={totalBusinessPages}
                page={businessPage}
                onChange={(e, page) => setBusinessPage(page)}
                color="primary"
              />
            </Box>
          </CardContent>
        </Card>
      </TabPanel>

      {/* Boards/Agendas Tab */}
      <TabPanel value={activeTab} index={3}>
        <Card>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              Agenda/Board Management ({totalBoards} total agendas)
            </Typography>

            <Box sx={{ display: 'flex', gap: 2, mb: 3, flexWrap: 'wrap' }}>
              <TextField
                size="small"
                label="Search agendas"
                value={boardFilters.search}
                onChange={(e) => setBoardFilters({ ...boardFilters, search: e.target.value })}
                sx={{ minWidth: 200 }}
              />
              <FormControl size="small" sx={{ minWidth: 150 }}>
                <InputLabel>Type</InputLabel>
                <Select
                  value={boardFilters.type}
                  onChange={(e) => setBoardFilters({ ...boardFilters, type: e.target.value })}
                  label="Type"
                >
                  <MenuItem value="">All Types</MenuItem>
                  {boardTypes.map((type) => (
                    <MenuItem key={type} value={type}>{type}</MenuItem>
                  ))}
                </Select>
              </FormControl>
              <Button
                variant="contained"
                onClick={() => {
                  setBoardPage(1);
                  loadBoards();
                }}
              >
                Apply Filters
              </Button>
            </Box>

            {loadingBoards && (
              <Box sx={{ p: 2 }}>
                <LinearProgress />
                <Typography variant="body2" sx={{ mt: 1, textAlign: 'center' }}>
                  Cargando agendas...
                </Typography>
              </Box>
            )}

            <TableContainer component={Paper} sx={{ maxHeight: 600 }}>
              <Table stickyHeader>
                <TableHead>
                  <TableRow>
                    <TableCell>Agenda Name</TableCell>
                    <TableCell>Type</TableCell>
                    <TableCell>Business</TableCell>
                    <TableCell>Owner</TableCell>
                    <TableCell>Reservations</TableCell>
                    <TableCell>Created</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {boards.map((board) => (
                    <TableRow key={board.id}>
                      <TableCell>
                        <Typography variant="subtitle2">{board.name}</Typography>
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={board.type}
                          color={getBoardTypeColor(board.type) as any}
                          size="small"
                          variant="outlined"
                        />
                      </TableCell>
                      <TableCell>
                        {board.business?.name || 'N/A'}
                      </TableCell>
                      <TableCell>
                        {board.business?.owner ? (
                          <Box>
                            <Typography variant="body2">{board.business.owner.name}</Typography>
                            <Typography variant="caption" color="text.secondary">
                              {board.business.owner.email}
                            </Typography>
                          </Box>
                        ) : (
                          <Typography variant="body2" color="text.secondary">No owner</Typography>
                        )}
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={board.reservationCount || 0}
                          color="primary"
                          size="small"
                          variant="outlined"
                        />
                      </TableCell>
                      <TableCell>
                        {new Date(board.createdAt).toLocaleDateString()}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>

            <Box sx={{ display: 'flex', justifyContent: 'center', mt: 3 }}>
              <Pagination
                count={totalBoardPages}
                page={boardPage}
                onChange={(e, page) => setBoardPage(page)}
                color="primary"
              />
            </Box>
          </CardContent>
        </Card>
      </TabPanel>

      {/* Reservations Tab */}
      <TabPanel value={activeTab} index={4}>
        <Card>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              Reservation Management ({totalReservations} total reservations)
            </Typography>

            <Box sx={{ display: 'flex', gap: 2, mb: 3, flexWrap: 'wrap' }}>
              <TextField
                size="small"
                label="Search client"
                value={reservationFilters.search}
                onChange={(e) => setReservationFilters({ ...reservationFilters, search: e.target.value })}
                sx={{ minWidth: 200 }}
              />
              <FormControl size="small" sx={{ minWidth: 150 }}>
                <InputLabel>Status</InputLabel>
                <Select
                  value={reservationFilters.status}
                  onChange={(e) => setReservationFilters({ ...reservationFilters, status: e.target.value })}
                  label="Status"
                >
                  <MenuItem value="">All Statuses</MenuItem>
                  <MenuItem value="confirmed">Confirmed</MenuItem>
                  <MenuItem value="pending">Pending</MenuItem>
                  <MenuItem value="cancelled">Cancelled</MenuItem>
                  <MenuItem value="completed">Completed</MenuItem>
                  <MenuItem value="no_show">No Show</MenuItem>
                </Select>
              </FormControl>
              <TextField
                size="small"
                label="Start Date"
                type="date"
                value={reservationFilters.startDate}
                onChange={(e) => setReservationFilters({ ...reservationFilters, startDate: e.target.value })}
                InputLabelProps={{ shrink: true }}
                sx={{ minWidth: 150 }}
              />
              <TextField
                size="small"
                label="End Date"
                type="date"
                value={reservationFilters.endDate}
                onChange={(e) => setReservationFilters({ ...reservationFilters, endDate: e.target.value })}
                InputLabelProps={{ shrink: true }}
                sx={{ minWidth: 150 }}
              />
              <Button
                variant="contained"
                onClick={() => {
                  setReservationPage(1);
                  loadReservations();
                }}
              >
                Apply Filters
              </Button>
            </Box>

            {loadingReservations && (
              <Box sx={{ p: 2 }}>
                <LinearProgress />
                <Typography variant="body2" sx={{ mt: 1, textAlign: 'center' }}>
                  Cargando reservas...
                </Typography>
              </Box>
            )}

            <TableContainer component={Paper} sx={{ maxHeight: 600 }}>
              <Table stickyHeader>
                <TableHead>
                  <TableRow>
                    <TableCell>Client</TableCell>
                    <TableCell>Contact</TableCell>
                    <TableCell>Agenda</TableCell>
                    <TableCell>Business</TableCell>
                    <TableCell>Date/Time</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell>Created</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {reservations.map((reservation) => (
                    <TableRow key={reservation.id}>
                      <TableCell>
                        <Typography variant="subtitle2">{reservation.clientName}</Typography>
                      </TableCell>
                      <TableCell>
                        <Box>
                          <Typography variant="body2">{reservation.clientEmail}</Typography>
                          <Typography variant="caption" color="text.secondary">
                            {reservation.clientPhone}
                          </Typography>
                        </Box>
                      </TableCell>
                      <TableCell>
                        {reservation.board?.name || 'N/A'}
                      </TableCell>
                      <TableCell>
                        {reservation.board?.business?.name || 'N/A'}
                      </TableCell>
                      <TableCell>
                        <Box>
                          <Typography variant="body2">
                            {new Date(reservation.startDate).toLocaleDateString()}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {new Date(reservation.startDate).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                          </Typography>
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={reservation.status}
                          color={getStatusColor(reservation.status) as any}
                          size="small"
                        />
                      </TableCell>
                      <TableCell>
                        {new Date(reservation.createdAt).toLocaleDateString()}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>

            <Box sx={{ display: 'flex', justifyContent: 'center', mt: 3 }}>
              <Pagination
                count={totalReservationPages}
                page={reservationPage}
                onChange={(e, page) => setReservationPage(page)}
                color="primary"
              />
            </Box>
          </CardContent>
        </Card>
      </TabPanel>

      <TabPanel value={activeTab} index={5}>
        <AnalyticsPanel />
      </TabPanel>

      <TabPanel value={activeTab} index={6}>
        <Typography variant="h6" gutterBottom>
          System Controls
        </Typography>
        <Alert severity="info">
          System monitoring and controls coming soon. This will include system health checks, maintenance modes, and configuration settings.
        </Alert>
      </TabPanel>

      <Dialog open={editUserDialog} onClose={() => setEditUserDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Edit User</DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
            <TextField
              label="Name"
              value={userForm.name}
              onChange={(e) => setUserForm({ ...userForm, name: e.target.value })}
              fullWidth
            />
            <TextField
              label="Email"
              value={userForm.email}
              onChange={(e) => setUserForm({ ...userForm, email: e.target.value })}
              fullWidth
            />
            <TextField
              label="Phone"
              value={userForm.phone}
              onChange={(e) => setUserForm({ ...userForm, phone: e.target.value })}
              fullWidth
            />
            <FormControl fullWidth>
              <InputLabel>Role</InputLabel>
              <Select
                value={userForm.role}
                onChange={(e) => setUserForm({ ...userForm, role: e.target.value })}
                label="Role"
              >
                <MenuItem value="business_owner">Business Owner</MenuItem>
                <MenuItem value="admin">Admin</MenuItem>
                <MenuItem value="super_admin">Super Admin</MenuItem>
              </Select>
            </FormControl>
            <FormControl fullWidth>
              <InputLabel>Subscription Plan</InputLabel>
              <Select
                value={userForm.subscriptionPlan}
                onChange={(e) => setUserForm({ ...userForm, subscriptionPlan: e.target.value })}
                label="Subscription Plan"
              >
                <MenuItem value="free">Free</MenuItem>
                <MenuItem value="basic">Basic</MenuItem>
                <MenuItem value="premium">Premium</MenuItem>
              </Select>
            </FormControl>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditUserDialog(false)}>Cancel</Button>
          <Button onClick={handleUpdateUser} variant="contained" color="primary">
            Update User
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={deleteUserDialog} onClose={() => setDeleteUserDialog(false)}>
        <DialogTitle>Delete User</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete user <strong>{userToDelete?.name}</strong>? This action cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteUserDialog(false)}>Cancel</Button>
          <Button onClick={handleDeleteUser} color="error" variant="contained">
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default SuperAdminDashboard;